// =============================================================================
// Ce qui s'ouvre à l'arrivée sur la page (DEV-2284, lot A ; D19).
//
// Ce que le courtier doit vivre : la page s'ouvre comme il l'a laissée (les
// temps 2, 3 et 4, puis « Remise du rapport » et « La démarche » quand elles
// existeront), sauf quand l'état de l'étude exige d'ouvrir une section, ou
// quand un lien demande d'ouvrir autre chose.
//
// - Il n'y a pas de réglage : il y a une MÉMOIRE de ce que chacun ouvre, dans
//   le navigateur, sous une clé qui contient l'identifiant de la personne et
//   celui de la société (deux collègues sur un poste ne la partagent pas).
// - Le stockage du navigateur peut être absent ou refuser l'accès (navigation
//   privée, données effacées) : chaque lecture et chaque écriture est gardée,
//   et la page s'ouvre alors comme au premier jour.
// - Priorité : l'état de l'étude, puis le geste (un lien `?ouvrir=`), puis la
//   mémoire de la personne, puis le premier jour (les temps ouverts, leurs
//   détails repliés : maquette allégée du 07/10/2026). Le lot C
//   formalise la règle par état ; ce module n'en porte que la mécanique.
// - `?ouvrir=` ne modifie jamais la mémoire du lecteur.
// =============================================================================

/** Les cinq ouvertures mémorisées : trois temps, puis l'accompagnement (lot D). */
export const SECTIONS_MEMORISEES = [
    "t2",
    "t3",
    "t4",
    "remise",
    "demarche",
] as const

export type SectionMemorisee = (typeof SECTIONS_MEMORISEES)[number]

/** `null` : la personne n'a rien décidé pour cette section. */
export type MemoireOuvertures = Record<SectionMemorisee, boolean | null>

export function memoireVide(): MemoireOuvertures {
    return { t2: null, t3: null, t4: null, remise: null, demarche: null }
}

/**
 * La clé nominative, ou `null` quand la personne ou la société n'est pas
 * connue : sans clé nominative, rien n'est gardé (jamais une clé partagée).
 */
export function cleDeMemoire(
    userId: string | null | undefined,
    companyId: string | null | undefined
): string | null {
    if (!userId || !companyId) return null
    return `optimturpe-v2:ouvertures:${userId}:${companyId}`
}

type Lecteur = Pick<Storage, "getItem"> | null | undefined
type Ecrivain = Pick<Storage, "setItem"> | null | undefined

/** Relit la mémoire. Toute valeur illisible vaut « rien de décidé ». */
export function lireMemoire(
    stockage: Lecteur,
    cle: string | null
): MemoireOuvertures {
    const memoire = memoireVide()
    if (!stockage || !cle) return memoire
    let brut: string | null = null
    try {
        brut = stockage.getItem(cle)
    } catch {
        return memoire
    }
    if (!brut) return memoire
    let lu: unknown
    try {
        lu = JSON.parse(brut)
    } catch {
        return memoire
    }
    if (!lu || typeof lu !== "object") return memoire
    for (const section of SECTIONS_MEMORISEES) {
        const v = (lu as Record<string, unknown>)[section]
        if (typeof v === "boolean") memoire[section] = v
    }
    return memoire
}

/** Écrit la mémoire ; un stockage qui refuse ne casse rien. */
export function ecrireMemoire(
    stockage: Ecrivain,
    cle: string | null,
    memoire: MemoireOuvertures
): boolean {
    if (!stockage || !cle) return false
    try {
        stockage.setItem(cle, JSON.stringify(memoire))
        return true
    } catch {
        return false
    }
}

/** Les valeurs admises de `?ouvrir=` : `tout` (tout déplier), `demarche` (le portefeuille). */
export type ParametreOuvrir = "tout" | "demarche"

export function lireParametreOuvrir(valeur: unknown): ParametreOuvrir | null {
    const v = Array.isArray(valeur) ? valeur[0] : valeur
    return v === "tout" || v === "demarche" ? v : null
}

/** Les temps repliables de la page. */
export type TempsRepliable = "t2" | "t3" | "t4"

/** Les deux sections d'accompagnement, mémorisées comme les temps (D16, D19). */
export type SectionAccompagnement = "remise" | "demarche"

export type OuverturesInitiales = Record<TempsRepliable, boolean> &
    Record<SectionAccompagnement, boolean> & {
        /** Vrai quand `?ouvrir=tout` demande aussi le cadre et les détails. */
        tout: boolean
    }

/**
 * Ce qui est ouvert à l'arrivée, dans l'ordre de priorité : état, geste,
 * mémoire, premier jour.
 */
export function ouverturesInitiales(p: {
    /** Ce que l'état ouvre d'office, quoi que la personne ait replié. */
    forces: Partial<Record<TempsRepliable, boolean>>
    ouvrir: ParametreOuvrir | null
    memoire: MemoireOuvertures
}): OuverturesInitiales {
    const resultat: OuverturesInitiales = {
        t2: false,
        t3: false,
        t4: false,
        remise: false,
        demarche: false,
        tout: p.ouvrir === "tout",
    }
    for (const t of ["t2", "t3", "t4"] as const) {
        if (p.forces[t]) resultat[t] = true
        else if (p.ouvrir === "tout") resultat[t] = true
        // Q36 (14/10/2026) : la demande vit dans « Rapport et demande » (t3).
        else if (p.ouvrir === "demarche" && t === "t3") resultat[t] = true
        else if (p.memoire[t] !== null) resultat[t] = p.memoire[t] as boolean
        // Maquette allégée (07/10/2026) : les temps s'ouvrent sur une phrase ou
        // un bloc de chiffres ; ce sont leurs détails qui restent repliés.
        else resultat[t] = true
    }
    // L'accompagnement : aucun état ne l'ouvre d'office tant que le journal
    // n'est pas servi ; le lien du portefeuille déroule « La démarche ».
    for (const a of ["remise", "demarche"] as const) {
        if (p.ouvrir === "tout") resultat[a] = true
        else if (p.ouvrir === "demarche" && a === "demarche") resultat[a] = true
        else resultat[a] = p.memoire[a] === true
    }
    return resultat
}

// =============================================================================
// Maquette finale du 15/10/2026 (DEV-2404) : le sommaire du résultat, huit
// rangées en trois accordéons. Tout est replié au repos ; ce que le lecteur
// ouvre reste ouvert (état de l'accordéon) ; « Études de ce compteur » s'ouvre
// d'office quand l'étude affichée n'est pas la recommandation ; une ancre
// ouvre sa rangée ; `?ouvrir=tout` ouvre tout, `?ouvrir=demarche` le rapport.
// =============================================================================

/** Les huit rangées du sommaire, dans l'ordre de la page. */
export const RANGEES_DU_SOMMAIRE = [
    "composantes",
    "depassements",
    "pourquoi",
    "points",
    "hypotheses",
    "comparaison",
    "rapport",
    "etudes",
] as const

export type RangeeDuSommaire = (typeof RANGEES_DU_SOMMAIRE)[number]

/** Les ancres de la page et la rangée qu'elles ouvrent. */
const RANGEE_DE_L_ANCRE: Record<string, RangeeDuSommaire> = {
    composantes: "composantes",
    "acc-postes": "composantes",
    depassements: "depassements",
    "m-t2": "depassements",
    pourquoi: "pourquoi",
    "acc-pourquoi": "pourquoi",
    points: "points",
    "acc-points": "points",
    hypotheses: "hypotheses",
    "m-cadre": "hypotheses",
    comparaison: "comparaison",
    "acc-comparaison": "comparaison",
    rapport: "rapport",
    "m-t3": "rapport",
    etudes: "etudes",
    "acc-etudes": "etudes",
}

/** La rangée qu'une ancre (`#points`, `#rapport`…) ouvre, ou `null`. */
export function rangeeDeLAncre(
    ancre: string | null | undefined
): RangeeDuSommaire | null {
    const cle = (ancre ?? "").replace(/^#/, "")
    return RANGEE_DE_L_ANCRE[cle] ?? null
}

/** Les rangées ouvertes à l'arrivée sur une étude. */
export function rangeesOuvertesInitiales(p: {
    ouvrir: ParametreOuvrir | null
    /** L'étude affichée n'est-elle pas la recommandation (étude saisie) ? */
    nonRecommandee: boolean
    ancre?: string | null
}): RangeeDuSommaire[] {
    if (p.ouvrir === "tout") return [...RANGEES_DU_SOMMAIRE]
    const ouvertes = new Set<RangeeDuSommaire>()
    if (p.nonRecommandee) ouvertes.add("etudes")
    if (p.ouvrir === "demarche") ouvertes.add("rapport")
    const parAncre = rangeeDeLAncre(p.ancre)
    if (parAncre) ouvertes.add(parAncre)
    return RANGEES_DU_SOMMAIRE.filter((r) => ouvertes.has(r))
}
